import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { ChevronRight, ChevronLeft, Check, Zap, Crown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface OnboardingData {
  useCase: string;
  customUseCase?: string;
  demoWatched: boolean;
  selectedPlan: string;
  billingCycle: 'monthly' | 'yearly' | 'lifetime';
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    useCase: "",
    customUseCase: "",
    demoWatched: false,
    selectedPlan: "",
    billingCycle: 'yearly'
  });

  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  const useCaseOptions = [
    { value: "school", label: "School & Education", description: "Homework, essays, academic writing" },
    { value: "seo", label: "SEO & Marketing", description: "Blog posts, web content, marketing copy" },
    { value: "skool", label: "Skool Courses", description: "Course content, educational materials" },
    { value: "business", label: "Business Content", description: "Reports, proposals, professional writing" },
    { value: "social", label: "Social Media", description: "Posts, captions, engagement content" },
    { value: "other", label: "Other", description: "Tell us your specific use case" }
  ];

  const plans = [
    {
      id: "free",
      name: "Free",
      description: "Get started with basic AI humanization",
      credits: "3",
      monthlyPrice: 0,
      yearlyPrice: 0,
      lifetimePrice: null,
      whopLinks: { monthly: null, yearly: null, lifetime: null },
      features: [
        "3 Humanizations / Visuals",
        "Basic Features",
        "Standard Processing",
        "Community Support"
      ],
      icon: Zap,
      popular: false
    },
    {
      id: "basic",
      name: "Basic",
      description: "Perfect for regular content creators",
      credits: "100",
      monthlyPrice: 9,
      yearlyPrice: 8.25,
      lifetimePrice: null,
      whopLinks: {
        monthly: "https://whop.com/checkout/plan_qWad2oY6Z7LJo?d2c=true",
        yearly: "https://whop.com/checkout/plan_LGAE6Vvcrc6i2?d2c=true"
      },
      features: [
        "100 Humanizations",
        "Copy & Download",
        "Multiple Visuals",
        "Email Support",
        "Mobile Preview"
      ],
      icon: Zap,
      popular: true
    },
    {
      id: "unlimited",
      name: "Unlimited",
      description: "For power users who need unlimited access",
      credits: "Unlimited",
      monthlyPrice: 20,
      yearlyPrice: 16.67,
      lifetimePrice: null,
      whopLinks: {
        monthly: "https://whop.com/checkout/plan_hE4PRuIAwKcn8?d2c=true",
        yearly: "https://whop.com/checkout/plan_SoKmnSPDDOdqJ?d2c=true"
      },
      features: [
        "Unlimited Humanizations",
        "Copy & Download",
        "Multiple Visuals",
        "Priority Processing",
        "Priority Support",
        "Advanced Analytics"
      ],
      icon: Crown,
      popular: false
    },
    {
      id: "lifetime",
      name: "Lifetime",
      description: "Pay once, use forever",
      credits: "Unlimited",
      monthlyPrice: null,
      yearlyPrice: null,
      lifetimePrice: 299,
      whopLinks: {
        monthly: null,
        yearly: null,
        lifetime: "https://whop.com/checkout/plan_QuZfuFJ0uXwSO?d2c=true"
      },
      features: [
        "Unlimited Humanizations",
        "Unlimited Visuals",
        "Copy & Download",
        "Priority Processing",
        "Priority Support",
        "Advanced Analytics",
        "Lifetime Updates",
        "No Recurring Fees"
      ],
      icon: Crown,
      popular: false
    }
  ];

  useEffect(() => {
    // Check if user has already completed onboarding
    const checkOnboardingStatus = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error checking onboarding status:', error);
        return;
      }

      if (data?.onboarding_completed) {
        navigate('/', { replace: true });
      }
    };

    checkOnboardingStatus();
  }, [user, navigate]);

  const handleUseCaseChange = (value: string) => {
    setOnboardingData(prev => ({
      ...prev,
      useCase: value,
      customUseCase: value === 'other' ? prev.customUseCase : ''
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleDemoComplete = () => {
    setOnboardingData(prev => ({ ...prev, demoWatched: true }));
    toast.success("Great! You're ready for the next step.");
  };

  const handlePlanSelect = (planId: string) => {
    setOnboardingData(prev => ({ ...prev, selectedPlan: planId }));
  };

  const handleComplete = async () => {
    if (!user) return;
    if (!onboardingData.selectedPlan) {
      toast.error("Please select a plan to continue.");
      return;
    }

    setLoading(true);
    try {
      const finalUseCase = onboardingData.useCase === 'other' ? onboardingData.customUseCase : onboardingData.useCase;

      const { error } = await supabase
        .from('profiles')
        .update({
          use_case: finalUseCase,
          demo_watched: onboardingData.demoWatched,
          selected_plan: onboardingData.selectedPlan,
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success("Welcome! Your account is all set up.");
      
      // If user selected a paid plan, open Whop checkout; also redirect to pricing as fallback
      if (onboardingData.selectedPlan !== 'free') {
        const selected = plans.find(p => p.id === onboardingData.selectedPlan) as any;
        let baseUrl: string | null | undefined = null;
        if (onboardingData.billingCycle === 'lifetime') {
          baseUrl = selected?.whopLinks?.lifetime;
        } else if (onboardingData.billingCycle === 'yearly') {
          baseUrl = selected?.whopLinks?.yearly;
        } else {
          baseUrl = selected?.whopLinks?.monthly;
        }
        if (baseUrl) {
          const url = user?.email ? `${baseUrl}&email=${encodeURIComponent(user.email!)}` : baseUrl;
          window.open(url, '_blank');
        }
        navigate('/pricing');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error("Failed to complete onboarding. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = onboardingData.useCase && (onboardingData.useCase !== 'other' || onboardingData.customUseCase);
  const canProceedStep2 = onboardingData.demoWatched;
  const canComplete = Boolean(onboardingData.selectedPlan);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <div className="w-full max-w-4xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">Step {currentStep} of {totalSteps}</span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Card className="w-full">
          {/* Step 1: Use Case */}
          {currentStep === 1 && (
            <>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">What will you use humanized content for?</CardTitle>
                <CardDescription>
                  This helps us personalize your experience and provide better recommendations.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <RadioGroup value={onboardingData.useCase} onValueChange={handleUseCaseChange}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {useCaseOptions.map((option) => {
                      const selected = onboardingData.useCase === option.value;
                      return (
                        <div
                          key={option.value}
                          className="flex items-center space-x-2 cursor-pointer"
                          onClick={() => handleUseCaseChange(option.value)}
                        >
                          <RadioGroupItem value={option.value} id={option.value} />
                          <Label htmlFor={option.value} className="flex-1 cursor-pointer">
                            <div className={`p-4 border rounded-lg transition-colors ${selected ? 'border-primary bg-muted/60' : 'hover:bg-muted/50'}`}>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-sm text-muted-foreground">{option.description}</div>
                            </div>
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </RadioGroup>

                {onboardingData.useCase === 'other' && (
                  <div className="space-y-2">
                    <Label htmlFor="custom-use-case">Please describe your use case:</Label>
                    <Textarea
                      id="custom-use-case"
                      placeholder="Tell us how you plan to use humanized content..."
                      value={onboardingData.customUseCase || ''}
                      onChange={(e) => setOnboardingData(prev => ({ ...prev, customUseCase: e.target.value }))}
                    />
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={handleNext} disabled={!canProceedStep1}>
                    Next <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Demo Video */}
          {currentStep === 2 && (
            <>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Watch Our Quick Demo</CardTitle>
                <CardDescription>
                  Learn how to get the best results from our humanization technology in just 2 minutes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/gospKwmzxTk"
                    title="Demo Video: How to Humanize Your Content"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                
                <div className="text-center">
                  <Button onClick={handleDemoComplete} variant={onboardingData.demoWatched ? "secondary" : "default"}>
                    {onboardingData.demoWatched ? (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Watched
                      </>
                    ) : (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Mark as Watched
                      </>
                    )}
                  </Button>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={handlePrevious}>
                    <ChevronLeft className="mr-2 h-4 w-4" /> Previous
                  </Button>
                  <Button onClick={handleNext} disabled={!canProceedStep2}>
                    Next <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Plan Selection */}
          {currentStep === 3 && (
            <>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Choose Your Plan</CardTitle>
                <CardDescription>
                  Select the plan that best fits your needs. You can always upgrade or downgrade later.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Billing Cycle Toggle */}
                <div className="flex justify-center">
                  <div className="flex items-center bg-muted rounded-lg p-1">
                    <Button
                      variant={onboardingData.billingCycle === 'monthly' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setOnboardingData(prev => ({ ...prev, billingCycle: 'monthly' }))}
                      className="rounded-md"
                    >
                      Monthly
                    </Button>
                    <Button
                      variant={onboardingData.billingCycle === 'yearly' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setOnboardingData(prev => ({ ...prev, billingCycle: 'yearly' }))}
                      className="rounded-md"
                    >
                      Yearly
                    </Button>
                    <Button
                      variant={onboardingData.billingCycle === 'lifetime' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setOnboardingData(prev => ({ ...prev, billingCycle: 'lifetime' }))}
                      className="rounded-md"
                    >
                      Lifetime 🤠
                    </Button>
                  </div>
                </div>

                {/* Plans */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {plans.map((plan) => {
                    const Icon = plan.icon;
                    const isSelected = onboardingData.selectedPlan === plan.id;
                      const isLifetimeCycle = onboardingData.billingCycle === 'lifetime';
                      const priceValue =
                        plan.id === 'free' ? 'Free' :
                        (plan.id === 'lifetime' && isLifetimeCycle) ? `$${Math.round(plan.lifetimePrice as number)}` :
                        (!isLifetimeCycle && (plan as any)[`${onboardingData.billingCycle}Price`] != null)
                          ? `$${Math.round(((plan as any)[`${onboardingData.billingCycle}Price`] as number))}`
                          : null;
                    
                    return (
                      <div
                        key={plan.id}
                        className={`relative p-6 border rounded-lg transition-all ${
                          plan.popular ? 'border-primary' : 'border-border'
                        }`}
                      >
                        {plan.popular && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                            <span className="bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full">
                              Most Popular
                            </span>
                          </div>
                        )}
                        
                        <div className="space-y-4">
                          {/* Header with checkbox */}
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-3">
                              <Checkbox
                                id={plan.id}
                                checked={isSelected}
                                onCheckedChange={() => handlePlanSelect(plan.id)}
                              />
                              <Icon className="h-6 w-6 text-primary" />
                            </div>
                          </div>

                          <div>
                            <h3 className="font-semibold text-lg">{plan.name}</h3>
                            <p className="text-sm text-muted-foreground">{plan.description}</p>
                          </div>

                            <div className="space-y-1">
                              <div className="flex items-baseline space-x-2">
                                <span className="text-2xl font-bold">
                                  {priceValue ?? '—'}
                                </span>
                                {priceValue && onboardingData.billingCycle !== 'lifetime' && plan.id !== 'free' && (
                                  <span className="text-sm text-muted-foreground">/mo{onboardingData.billingCycle === 'yearly' ? ' (billed yearly)' : ''}</span>
                                )}
                                {priceValue && onboardingData.billingCycle === 'lifetime' && plan.id === 'lifetime' && (
                                  <span className="text-sm text-muted-foreground">once</span>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">{plan.credits === 'Unlimited' ? 'Unlimited credits' : `${plan.credits} credits`}</div>
                            </div>

                          <ul className="space-y-2 text-sm">
                            {plan.features.map((feature, index) => (
                              <li key={index} className="flex items-center">
                                <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                          {plan.id !== 'free' && (
                            <Button
                              variant="outline"
                              className="w-full mt-4"
                              onClick={() => {
                                let baseUrl: string | null | undefined = null;
                                if (onboardingData.billingCycle === 'lifetime') {
                                  baseUrl = plan.whopLinks?.lifetime;
                                } else if (onboardingData.billingCycle === 'yearly') {
                                  baseUrl = plan.whopLinks?.yearly;
                                } else {
                                  baseUrl = plan.whopLinks?.monthly;
                                }
                                if (!baseUrl) {
                                  toast.error('Checkout unavailable for this billing option.');
                                  return;
                                }
                                const url = user?.email ? `${baseUrl}&email=${encodeURIComponent(user.email!)}` : baseUrl;
                                window.open(url, '_blank');
                              }}
                            >
                              Go to Checkout
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={handlePrevious}>
                    <ChevronLeft className="mr-2 h-4 w-4" /> Previous
                  </Button>
                  <Button 
                    onClick={handleComplete} 
                    disabled={!canComplete || loading}
                    className="min-w-[120px]"
                  >
                    {loading ? "Setting up..." : "Complete Setup"}
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}